import { useCallback, useContext } from 'react';
import RangeSlider from '@/srp/FiltersMenu/RangeSlider';
import { MIN_MONTHLY_PAYMENT } from '~/constants/srp';
import FilterEvents from '~/constants/srp/events';
import { useIsTradeInApplied } from '~/context/common/TradeContext';
import {
  useHasSoftPull,
  useIsFinanceInitialized
} from '~/context/srp/FinanceContext';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import SearchContext from '~/context/srp/SearchContext';
import {
  getCashDownDescription,
  getMonthlyPaymentDescription
} from '~/utilities/srp/budgetCopy';
import FinancingCard from './FinancingCard';
import TradeCard from './TradeCard';
import BudgetContext from '../BudgetContext';

type OnUpdate = (_?: number[]) => void;

type OwnProps = {
  isShowing: boolean;
};

export default function BudgetFinance({ isShowing }: OwnProps) {
  const {
    currentDownPayment,
    currentMonthlyPayment,
    maxMonthlyPayment,
    minDownPayment,
    minDownPaymentNoTrade,
    setCurrentDownPayment,
    setCurrentMonthlyPayment
  } = useContext(BudgetContext);
  const { showFinance } = useContext(SearchContext);
  const hasValidSoftPull = useHasSoftPull();
  const isFinanceInitialized = useIsFinanceInitialized();
  const trackEvent = useTrackEvent();
  const isTradeInApplied = useIsTradeInApplied();

  const onUpdateDownPayment = useCallback<OnUpdate>(
    range => {
      const nextValue = range?.at(0);

      if (nextValue === currentDownPayment) return;

      setCurrentDownPayment(nextValue);

      trackEvent(FilterEvents.engagedFacet, {
        action: 'add facet',
        item: 'slider',
        label: 'downPayment',
        value: nextValue
      });
    },
    [currentDownPayment, setCurrentDownPayment, trackEvent]
  );

  const onUpdateMonthlyPayment = useCallback<OnUpdate>(
    range => {
      const nextValue = range?.at(0);

      if (nextValue === currentMonthlyPayment) return;

      setCurrentMonthlyPayment(nextValue);

      trackEvent(FilterEvents.engagedFacet, {
        action: 'add facet',
        item: 'slider',
        label: 'monthlyPayment',
        value: nextValue
      });
    },
    [currentMonthlyPayment, setCurrentMonthlyPayment, trackEvent]
  );

  return (
    <>
      <div className="grid gap-32">
        <div>
          <RangeSlider
            isShowing={isShowing}
            id="monthly-payment"
            label="Max Monthly Payment"
            description={getMonthlyPaymentDescription({
              hasValidSoftPull,
              isFinanceInitialized,
              maxMonthlyPayment
            })}
            format="dollars"
            labelTextTransform="uppercase"
            min={MIN_MONTHLY_PAYMENT}
            max={maxMonthlyPayment}
            step={50}
            initialValues={[currentMonthlyPayment ?? maxMonthlyPayment]}
            onUpdate={onUpdateMonthlyPayment}
          />
        </div>

        <div>
          <RangeSlider
            isShowing={isShowing}
            id="down-payment"
            label="Max Cash Down Payment"
            description={getCashDownDescription({
              hasValidSoftPull,
              isFinanceInitialized,
              hasAppliedTrade: isTradeInApplied,
              minDownPayment,
              minDownPaymentNoTrade
            })}
            format="dollars"
            labelTextTransform="uppercase"
            min={minDownPayment}
            max={minDownPayment + 10000}
            step={500}
            initialValues={[currentDownPayment ?? minDownPayment]}
            onUpdate={onUpdateDownPayment}
          />
        </div>
      </div>

      {showFinance && <FinancingCard />}

      <TradeCard />
    </>
  );
}
